import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePreviewCourseMutation } from "../services/auth";

const PreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [previewCourse, { isLoading }] = usePreviewCourseMutation();
  
  const [showModal, setShowModal] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function validateAndOpen(e?: React.FormEvent) {
    e?.preventDefault();
    setError("");
    setSuccess("");

    // Frontend validation
    if (!firstName.trim() || !email.trim() || !whatsapp.trim()) {
      setError("Please provide your first name, email and WhatsApp number.");
      return;
    }

    // Email validation
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    // WhatsApp validation (loose)
    if (!/^[\d +()-]{7,20}$/.test(whatsapp)) {
      setError("Please enter a valid WhatsApp number.");
      return;
    }

    try {
      // Call API to register preview access
      const response = await previewCourse({
        firstName: firstName.trim(),
        email: email.trim(),
        whatsapp: whatsapp.trim(),
      }).unwrap();

      console.log("✅ Preview access granted:", response);

      // Show success message
      setSuccess(
        response.message || "Preview access granted! Check your email for details."
      );

      // Close modal after 1.5 seconds
      setTimeout(() => {
        setShowModal(false);
      }, 1500);
    } catch (err: any) {
      console.error("❌ Preview registration error:", err);
      setError(
        err?.data?.error ||
          err?.data?.message ||
          "Failed to register for preview. Please try again."
      );
    }
  }

  function skipPreview() {
    // Optional: track skip event
    console.log("User skipped preview registration");
    navigate("/");
  }

  return (
    <main className="min-h-screen bg-gray-50 text-slate-900 p-6">
      {/* Modal overlay */}
      {showModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4">
          <div className="max-w-md w-full bg-white rounded-xl p-6 shadow-lg">
            <h2 className="text-xl font-semibold mb-2">
              Access Course Preview
            </h2>
            <p className="text-sm text-slate-600 mb-4">
              Submit your first name, email and WhatsApp number to view a
              preview lesson. We'll also send you the preview link via email!
            </p>

            <form onSubmit={validateAndOpen} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="John"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="you@example.com"
                  type="email"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  WhatsApp number <span className="text-red-500">*</span>
                </label>
                <input
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="+234 812 345 6789"
                  required
                  disabled={isLoading}
                />
                <p className="text-xs text-slate-500 mt-1">
                  We'll send you bonus content via WhatsApp
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-800">{success}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={skipPreview}
                  className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={isLoading}
                >
                  Skip
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : "View Preview"}
                </button>
              </div>
            </form>

            <p className="text-xs text-slate-500 text-center mt-4">
              By submitting, you agree to receive course updates via email and
              WhatsApp
            </p>
          </div>
        </div>
      )}

      {/* Preview content */}
      <section className="max-w-5xl mx-auto mt-8">
        <header className="mb-6">
          <h1 className="text-3xl font-bold">
            Course Preview — Digital Income Blueprint
          </h1>
          <p className="text-slate-600 mt-2">
            A short sample lesson to show the quality and structure of the DIB
            modules.
          </p>
        </header>

        <article className="bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-semibold mb-3">
            Preview Lesson: Sweet-Spot Validation (excerpt)
          </h2>

          {/* If modal still shown, blur content */}
          <div className={`${showModal ? "blur-sm pointer-events-none" : ""}`}>
            <div className="aspect-video bg-gradient-to-br from-teal-50 to-blue-50 rounded-lg overflow-hidden mb-4 flex items-center justify-center border border-gray-200">
              {/* Placeholder for video embed */}
              <div className="text-center p-8">
                <svg
                  className="w-20 h-20 mx-auto mb-4 text-teal-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-slate-700 font-medium">Preview Video</p>
                <p className="text-sm text-slate-500">
                  Submit your details to unlock
                </p>
              </div>
            </div>

            <div className="prose max-w-none">
              <p className="text-slate-700 mb-4">
                In this excerpt you will learn the simplest validation test that
                tells you whether your idea can sell in the Nigerian market —
                fast.
              </p>

              <h3 className="text-lg font-semibold mb-2 text-slate-900">
                The 3-Step Validation Framework:
              </h3>

              <ol className="list-decimal pl-6 text-slate-700 space-y-3 mb-6">
                <li>
                  <strong>Define one clear customer avatar</strong> with their
                  top pain point
                </li>
                <li>
                  <strong>Describe a 1-step solution</strong> (the smallest
                  possible product)
                </li>
                <li>
                  <strong>Run a 7-day test:</strong> 10 conversations + 1
                  landing page
                </li>
              </ol>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                <p className="text-sm text-blue-800 font-medium mb-1">
                  📥 Bonus Resources Included
                </p>
                <p className="text-sm text-blue-700">
                  Download the Sweet-Spot Validation worksheet and Customer
                  Avatar template after watching the preview.
                </p>
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-4">
                <p className="text-sm text-teal-800 font-medium mb-1">
                  🎓 Ready for the Full Course?
                </p>
                <p className="text-sm text-teal-700 mb-3">
                  This is just a taste! The complete Digital Income Blueprint
                  includes:
                </p>
                <ul className="text-sm text-teal-700 space-y-1">
                  <li>✅ 6 comprehensive modules with 40+ video lessons</li>
                  <li>✅ Step-by-step workbooks and templates</li>
                  <li>✅ Live Q&A sessions and community support</li>
                  <li>✅ Real Nigerian case studies and examples</li>
                </ul>
              </div>
            </div>
          </div>
        </article>

        {!showModal && (
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate("/sl")}
              className="px-8 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-lg"
            >
              Enroll Now - Get Full Access
            </button>
            <p className="text-sm text-slate-500 mt-2">
              Limited spots available for this cohort
            </p>
          </div>
        )}
      </section>
    </main>
  );
};

export default PreviewPage;